import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { toast } from '../../store/toastStore';
import { toastDebtWarning } from '../../utils/debtWarning';
import { toastWorkLogNotices } from '../../utils/workLogNotices';
import { askWorkEngagement } from '../timer/timerFlow';
import { announce } from '../ui/LiveAnnouncer';
import { localDateKey } from '../home/homeModel';
import type { WorkLogSuggestion, WorkLogSuggestionsData } from '../../types';
import {
  acceptPayload, acceptWithEngagement, bulkOutcome, defaultDraft, draftProblem, type DraftProblem, type SuggestionDraft,
} from './documentationModel';

/** The react-query key of one day's suggestions (brief: `['work-log-suggestions', date]`). */
export const suggestionsKey = (date: string) => ['work-log-suggestions', date] as const;

/** Today in local time (the app runs in Israel time, as the server's default). */
export const todaySuggestionsDate = () => localDateKey(new Date());

const EMPTY = (date: string): WorkLogSuggestionsData => ({ date, attendance_minutes: 0, documented_minutes: 0, suggestions: [] });

export async function fetchSuggestions(date: string): Promise<WorkLogSuggestionsData> {
  const res = await api.getWorkLogSuggestions(date);
  const data = res?.data;
  if (!data) return EMPTY(date);
  return { ...EMPTY(date), ...data, suggestions: Array.isArray(data.suggestions) ? data.suggestions : [] };
}

/** Fetch for a one-off decision (clock-out), sharing the list's cache. */
export function fetchSuggestionsCached(queryClient: QueryClient, date = todaySuggestionsDate()) {
  return queryClient.fetchQuery({ queryKey: suggestionsKey(date), queryFn: () => fetchSuggestions(date), staleTime: 15_000 });
}

/**
 * "תיעוד מוצע" of one day for the signed-in employee (admin / agent only, as
 * the endpoint). Computed by the server per request; kept fresh for 30 s.
 */
export function useWorkLogSuggestions(date: string = todaySuggestionsDate(), enabled = true) {
  const role = useAuthStore((s) => s.user?.role);
  const isStaff = role === 'admin' || role === 'agent';
  return useQuery({
    queryKey: suggestionsKey(date),
    queryFn: () => fetchSuggestions(date),
    enabled: enabled && isStaff,
    staleTime: 30_000,
    retry: 1,
  });
}

/** What keeps a row on screen after a try: a field to fix, or the server's answer. */
export type SuggestionRowError = { problem: DraftProblem } | { server: unknown; action: 'accept' | 'dismiss' };

/** Everything a confirm or dismiss can change (brief: suggestions, daily status, timers, attendance). */
function invalidateAfter(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ['work-log-suggestions'] });
  queryClient.invalidateQueries({ queryKey: ['dashboard-daily-status'] });
  queryClient.invalidateQueries({ queryKey: ['timer-status'] });
  queryClient.invalidateQueries({ queryKey: ['attendance-status'] });
  queryClient.invalidateQueries({ queryKey: ['work-logs'] });
  queryClient.invalidateQueries({ queryKey: ['work-records'] });
}

/**
 * The list's state and actions, shared by the Home peek and "סגירת יום":
 * the row drafts (only what the employee changed is kept; the rest follows
 * the server), per-row errors, confirm / dismiss one row, and "אישור הכול"
 * (sequential; a row that fails stays with its error, the others go).
 */
export function useSuggestionsController(date: string = todaySuggestionsDate()) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const query = useWorkLogSuggestions(date);
  const rows = query.data?.suggestions ?? [];
  const [edits, setEdits] = useState<Record<string, Partial<SuggestionDraft>>>({});
  const [errors, setErrors] = useState<Record<string, SuggestionRowError>>({});
  const [busy, setBusy] = useState<Record<string, 'accept' | 'dismiss'>>({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const editsRef = useRef(edits);
  editsRef.current = edits;

  const draftOf = useCallback((s: WorkLogSuggestion): SuggestionDraft => ({ ...defaultDraft(s), ...editsRef.current[s.key] }), []);

  const setError = (key: string, err: SuggestionRowError | null) => setErrors((old) => {
    const next = { ...old };
    if (err) next[key] = err; else delete next[key];
    return next;
  });
  const setRowBusy = (key: string, what: 'accept' | 'dismiss' | null) => setBusy((old) => {
    const next = { ...old };
    if (what) next[key] = what; else delete next[key];
    return next;
  });

  const setDraft = (key: string, patch: Partial<SuggestionDraft>) => {
    setEdits((old) => ({ ...old, [key]: { ...old[key], ...patch } }));
    // A field error goes once the employee changes something; a server error stays until the next try.
    setErrors((old) => (old[key] && 'problem' in old[key] ? (({ [key]: _gone, ...rest }) => rest)(old) : old));
  };

  /** Take the row out of the cached list at once (the refetch confirms it). */
  const removeRow = (key: string, minutes = 0) => {
    queryClient.setQueryData<WorkLogSuggestionsData>(suggestionsKey(date), (old) => (old ? {
      ...old,
      documented_minutes: old.documented_minutes + minutes,
      suggestions: old.suggestions.filter((s) => s.key !== key),
    } : old));
  };

  /** One confirm, without the shared refresh (so "אישור הכול" refreshes once). */
  // D-291: `override` = draft fields to use right now (the shortened minutes), kept in the row's draft too.
  const acceptRow = async (s: WorkLogSuggestion, override?: Partial<SuggestionDraft>): Promise<boolean> => {
    if (override) {
      const { confirmParallel: _once, ...keep } = override; // D-299: the confirmation is for this accept only
      setEdits((old) => ({ ...old, [s.key]: { ...old[s.key], ...keep } }));
    }
    const draft = { ...draftOf(s), ...override };
    const problem = draftProblem(s, draft);
    if (problem) { setError(s.key, { problem }); return false; }
    setRowBusy(s.key, 'accept');
    setError(s.key, null);
    try {
      const body = acceptPayload(s, draft);
      // BQ-052: a task with no engagement - the server asks which one, the picker answers, the accept is repeated.
      const taskId = s.ticket_id ?? draft.ticketId ?? '';
      const res = await acceptWithEngagement(
        body,
        (b) => api.acceptWorkLogSuggestion(b),
        (engagements) => askWorkEngagement({ task: { id: taskId, title: s.ticket_title || '' }, engagements, mode: 'log' })
      );
      if (res === null) return false; // the picker was closed: nothing saved, the row stays
      toastDebtWarning(res); // D-110
      toastWorkLogNotices(res); // D-266 sec.1: saved over the quota / locked
      removeRow(s.key, s.kind === 'open_record' ? 0 : body.minutes);
      return true;
    } catch (err) {
      setError(s.key, { server: err, action: 'accept' });
      return false;
    } finally {
      setRowBusy(s.key, null);
    }
  };

  const accept = async (s: WorkLogSuggestion, override?: Partial<SuggestionDraft>): Promise<boolean> => {
    const ok = await acceptRow(s, override);
    if (ok) {
      announce(t('documentation.accepted'));
      invalidateAfter(queryClient);
    } else {
      announce(t('documentation.rowFailed'), 'assertive');
    }
    return ok;
  };

  const dismiss = async (s: WorkLogSuggestion): Promise<boolean> => {
    setRowBusy(s.key, 'dismiss');
    setError(s.key, null);
    try {
      await api.dismissWorkLogSuggestion(s.key);
      removeRow(s.key);
      announce(t('documentation.dismissed'));
      invalidateAfter(queryClient);
      return true;
    } catch (err) {
      setError(s.key, { server: err, action: 'dismiss' });
      announce(t('documentation.rowFailed'), 'assertive');
      return false;
    } finally {
      setRowBusy(s.key, null);
    }
  };

  /** "אישור הכול": every row with its current values, one by one. */
  const acceptAll = async (): Promise<{ accepted: number; failed: number }> => {
    const list = [...rows];
    if (list.length === 0) return { accepted: 0, failed: 0 };
    setBulkBusy(true);
    const results: boolean[] = [];
    try {
      for (const s of list) results.push(await acceptRow(s));
    } finally {
      setBulkBusy(false);
    }
    const outcome = bulkOutcome(results);
    if (outcome.accepted > 0) invalidateAfter(queryClient);
    if (outcome.failed === 0) {
      toast('success', t('documentation.acceptedAll', { count: outcome.accepted }));
    } else {
      toast('warning', t('documentation.acceptedSome', { count: outcome.failed, accepted: outcome.accepted }));
    }
    return outcome;
  };

  return {
    date,
    query,
    rows,
    data: query.data ?? null,
    draftOf,
    setDraft,
    errors,
    busy,
    bulkBusy,
    accept,
    dismiss,
    acceptAll,
  };
}

export type SuggestionsController = ReturnType<typeof useSuggestionsController>;
